import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { LogOut, User as UserIcon, CreditCard } from "lucide-react";
import { startGoogleSignIn } from "@/lib/googleAuth";

export default function Header({ transparent = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        transparent ? "bg-transparent" : "bg-black/60 backdrop-blur-xl border-b border-white/10"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <span className="font-display font-black text-2xl md:text-3xl tracking-tighter text-[#E50914]">
            YBTV
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-gray-300">
          <Link to="/browse" data-testid="nav-home" className="hover:text-white transition-colors">Home</Link>
          <Link to="/browse?type=movie" data-testid="nav-movies" className="hover:text-white transition-colors">Movies</Link>
          <Link to="/browse?type=show" data-testid="nav-shows" className="hover:text-white transition-colors">Shows</Link>
          <Link to="/browse?type=trailer" data-testid="nav-trailers" className="hover:text-white transition-colors">Trailers</Link>
          <Link to="/browse?type=interview" data-testid="nav-interviews" className="hover:text-white transition-colors">Interviews</Link>
        </nav>

        <div className="flex items-center gap-3">
          {!user ? (
            <>
              <Button
                data-testid="signin-btn"
                onClick={startGoogleSignIn}
                className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-6 font-semibold"
              >
                Sign in with Google
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <span
                data-testid="signed-in-pill"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#E50914]/10 border border-[#E50914]/30 px-3 py-1 text-xs text-white font-medium"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" />
                Signed in{user.is_subscribed ? " · Premium" : ""}
              </span>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full hover:bg-white/5 p-1 transition">
                  <Avatar className="h-9 w-9 ring-1 ring-white/20">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-[#E50914] text-white text-sm">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 bg-[#141419] border-white/10 text-white"
              >
                <DropdownMenuLabel className="text-gray-300">
                  <div className="font-semibold text-white truncate">{user.name}</div>
                  <div className="text-xs text-gray-400 truncate">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")} className="cursor-pointer focus:bg-white/5">
                  <UserIcon className="w-4 h-4 mr-2" /> Account
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-billing" onClick={() => navigate("/account")} className="cursor-pointer focus:bg-white/5">
                  <CreditCard className="w-4 h-4 mr-2" /> Membership
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer focus:bg-white/5 text-[#E50914]">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
